export interface TargetBox {
  x: number;
  y: number;
  width: number;
  height: number;
  viewportWidth?: number;
  viewportHeight?: number;
  /** Support snake_case schema from older runner outputs */
  viewport_width?: number;
  viewport_height?: number;
}

export type HighlightTone =
  | "destructive"
  | "warning"
  | "neutral"
  | "success"
  | "nit"
  | "helps"
  | "hinders"
  | "important"
  | "critical";

export interface ScreenshotHighlight {
  id: string;
  box: TargetBox;
  tone?: HighlightTone | string | null;
  label?: string | null;
  isActive?: boolean;
  isDimmed?: boolean;
  index?: number;
}

export function isValidBox(
  box: TargetBox | null | undefined,
): box is TargetBox {
  return Boolean(
    box &&
      Number.isFinite(box.x) &&
      Number.isFinite(box.y) &&
      Number.isFinite(box.width) &&
      Number.isFinite(box.height) &&
      box.width > 0 &&
      box.height > 0,
  );
}

export function resolveBoxViewport(
  box: TargetBox | null | undefined,
  natural: { width: number; height: number } | null,
): { vw: number; vh: number } {
  const explicitW = box?.viewportWidth || box?.viewport_width;
  const explicitH = box?.viewportHeight || box?.viewport_height;
  if (explicitW && explicitH && explicitW > 0 && explicitH > 0) {
    return { vw: explicitW, vh: explicitH };
  }

  const minRequiredW = box ? box.x + box.width : 0;
  const minRequiredH = box ? box.y + box.height : 0;

  let w =
    natural?.width && natural.width >= minRequiredW && natural.width >= 600
      ? natural.width
      : 1280;
  let h =
    natural?.height && natural.height >= minRequiredH && natural.height >= 400
      ? natural.height
      : 800;

  if (minRequiredW > w) {
    w = minRequiredW > 1440 ? 1920 : 1440;
  }
  if (minRequiredH > h) {
    h = minRequiredH > 900 ? 1080 : 900;
  }

  return { vw: Math.max(1, w), vh: Math.max(1, h) };
}

export function resolveHighlightStyles(tone?: HighlightTone | string | null): {
  stroke: string;
  fill: string;
  badgeBg: string;
  badgeText: string;
} {
  switch (tone) {
    case "warning":
    case "important":
      return {
        stroke: "#f59e0b",
        fill: "rgba(245, 158, 11, 0.15)",
        badgeBg: "#78350f",
        badgeText: "#fef3c7",
      };
    case "success":
    case "helps":
      return {
        stroke: "#10b981",
        fill: "rgba(16, 185, 129, 0.15)",
        badgeBg: "#064e3b",
        badgeText: "#d1fae5",
      };
    case "neutral":
    case "nit":
      return {
        stroke: "#0ea5e9",
        fill: "rgba(14, 165, 233, 0.15)",
        badgeBg: "#0c4a6e",
        badgeText: "#e0f2fe",
      };
    default:
      return {
        stroke: "#ef4444",
        fill: "rgba(239, 68, 68, 0.15)",
        badgeBg: "#7f1d1d",
        badgeText: "#fee2e2",
      };
  }
}
